// utils/anthropometry/calculations.js
//Calcular IMC
const calculateBMI = (height, weight) => {
  if (!height || !weight) return NaN;
  return (weight / (height * height)).toFixed(2);
};
//Calcular Peso Teórico
const calculateTheoreticalWeight = (height, gender) => {
  const baseWeight = height - 100;
  let percentage = 0;

  if (gender === 'male') {
    if (height >= 140 && height <= 155) percentage = 0;
    else if (height > 155 && height <= 170) percentage = 5;
    else if (height > 170) percentage = 10;
  } else if (gender === 'female') {
    if (height >= 140 && height <= 150) percentage = 0;
    else if (height > 150 && height <= 165) percentage = 10;
    else if (height > 165) percentage = 15;
  }

  return baseWeight - (baseWeight * percentage / 100);
};

//Calcular Altura Estimada
const calculateEstimatedHeight = (age, kneeHeight, gender) => {
  if (gender === 'male') {
    return (64.19 - (0.04 * age) + (2.02 * kneeHeight)).toFixed(2);
  }
  if (gender === 'female') {
    return (84.88 - (0.24 * age) + (1.83 * kneeHeight)).toFixed(2);
  }
  return 'Undefined';
};


const hasRequiredSkinfolds = (equationCode, skinfolds) => {
  const required = {
    '1': ['triceps', 'subscapular', 'suprailiac'],
    '2': ['chest', 'abdominal', 'thigh', 'triceps', 'subscapular', 'suprailiac', 'axillary'],
    '3': ['triceps', 'biceps', 'subscapular', 'suprailiac'],
    '4': ['biceps', 'triceps', 'subscapular', 'suprailiac'],
  };

  const requiredKeys = required[equationCode] || [];

  return requiredKeys.every(key => {
    const value = Number(skinfolds[key]);
    return !isNaN(value) && value > 0;
  });
};

//Calcular BMR - Body 
const calculateBMR = (gender, theoreticalWeight, currentWeight, heightInMeters, age) => {
  const weight = age <= 10 ? currentWeight : theoreticalWeight;
  if (!weight || !heightInMeters || !age) return 0;

  let bmr = 0;
  if (gender === 'male') {
    bmr = 88.36 + (13.4 * weight) + (4.8 * heightInMeters * 100) - (5.7 * age);
  } else if (gender === 'female') {
    bmr = 447.6 + (9.2 * weight) + (3.1 * heightInMeters * 100) - (4.3 * age);
  }
  return bmr.toFixed(2);
};

const calculateTEE = (bmr, activityLevel) => {
  if (!bmr || !activityLevel) return 0;
  return (bmr * activityLevel).toFixed(2);
};

//Calcular Peso da Massa Gorda
const calculateFatMassWeight = (weight, bodyFatPercentage) => {
  return (weight * (bodyFatPercentage / 100)).toFixed(2);
};

//Calcular Massa Magra Corporal
const calculateLeanBodyMass = (weight, fatMassWeight) => {
  return (weight - fatMassWeight).toFixed(2);
};

//Calcular Tipo do peso magro
const calculateLeanWeightType = (leanMass, gender) => {
  const lean = Number(leanMass);
  if (isNaN(lean)) return 'Undefined';
  if (gender === 'male') return lean < 50 ? 'Low' : 'Normal';
  return lean < 40 ? 'Low' : 'Normal';
};

const calculateTotalBodyWater = (leanBodyMass) => {
  return (leanBodyMass * 0.73).toFixed(2);
};

const calculateArmMuscleCircumference = (ac, tsf) => {
  const pi = Math.PI;
  const acNumber = Number(ac);
  const tsfNumber = Number(tsf) / 10;
  if (acNumber <= 0 || tsfNumber <= 0) return 'N/A';
  const amc = acNumber - (pi * tsfNumber);
  return amc <= 0 ? 'Invalid value' : amc.toFixed(2);
};

const calculateArmMuscleArea = (ac, tsf) => {
  const pi = Math.PI;
  const acNumber = Number(ac);
  const tsfNumber = Number(tsf) / 10;
  if (!acNumber || !tsfNumber) return 'N/A';
  const amc = acNumber - (pi * tsfNumber);
  if (amc <= 0) return 'Invalid value';
  return ((amc * amc) / (4 * pi)).toFixed(2);
};

const calculateCorrectedArmMuscleArea = (ac, tsf, gender) => {
  const pi = Math.PI;
  const acNumber = Number(ac);
  const tsfNumber = Number(tsf) / 10;
  if (!acNumber || !tsfNumber) return 'N/A';
  const base = acNumber - (pi * tsfNumber);
  const correction = gender === 'male' ? 10 : 6.5;
  return ((base * base) / (4 * pi) - correction).toFixed(2);
};

const calculateArmArea = (ac) => {
  const pi = Math.PI;
  return ((pi * Math.pow(ac, 2)) / 4).toFixed(2);
};

const calculateArmFatArea = (aa, ama) => {
  return (aa - ama).toFixed(2);
};

// Peso Estimado Atual
const calculateEstimatedCurrentWeight = (estimatedHeight, bmiReference = 22) => {
  return (bmiReference * ((estimatedHeight / 100) ** 2)).toFixed(2);
};

// Relação Cintura-Quadril
const calculateWHR = (waistCircumference, hipCircumference) => {
  if (!waistCircumference || !hipCircumference) return null;
  return (waistCircumference / hipCircumference).toFixed(2);
};

// Massa Residual
const calculateResidualMass = (weight, fatMass, leanMass) => {
  const residual = weight - fatMass - leanMass;
  const fixed = Number(residual.toFixed(2));
  return fixed === -0 ? 0 : fixed;
};

// Excesso de Peso
const calculateWeightExcess = (currentWeight, idealWeight) => {
  const diff = currentWeight - idealWeight;
  return diff > 0 ? diff.toFixed(2) : '0.00';
};

// Déficit de Peso
const calculateWeightDeficit = (currentWeight, idealWeight) => {
  const diff = idealWeight - currentWeight;
  return diff > 0 ? diff.toFixed(2) : '0.00';
};

//Calcular CMB - Circunferência Muscular do Braço
const calculateCMB = (cb, triceps) => {
  const armCirc = Number(cb);
  const tricepsFold = Number(triceps);
  if (isNaN(armCirc) || isNaN(tricepsFold)) return undefined;

  const cmb = armCirc - (Math.PI * (tricepsFold / 10));
  return Number(cmb.toFixed(2)); // Retorna com 2 casas decimais
};

// Peso Ideal com IMC padrão 22
const calculateIdealWeight = (height, bmiReference = 22) => {
  if (!height) return null;
  return (bmiReference * ((height / 100) ** 2)).toFixed(2);
};

//Calculate AMB - Área Muscular do Braço
const calculateAMB = (cb, triceps) => {
  const cbVal = Number(cb);
  const tricepsVal = Number(triceps);
  if (isNaN(cbVal) || isNaN(tricepsVal)) return undefined;

  const cb2 = Math.pow(cbVal, 2);
  const dcCm = tricepsVal / 10;
  const amb = (cb2 / (4 * Math.PI)) - ((dcCm * cbVal) / (2 * Math.PI));
  return Number(amb.toFixed(2));
};

//Calculate AMBc - Área Muscular do Braço Corrigida
const calculateAMBc = (amb) => {
  const ambVal = Number(amb);
  if (isNaN(ambVal)) return undefined;

  const ambc = ambVal - (Math.PI * 10);
  return Number(ambc.toFixed(2));
};

const calculateAge = (birthDateString) => {
  const today = new Date();
  const birthDate = new Date(birthDateString);

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (months < 0 || (months === 0 && days < 0)) {
    years--;
    months += 12;
  }

  if (days < 0) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
    months--;
  }

  return { years, months, days };
};

// Peso da Massa Corporal
const calculateMuscleMassWeight = (leanBodyMass, totalBodyWater, residualMass) => {
  const lbm = Number(leanBodyMass);
  const tbw = Number(totalBodyWater);
  const rm = Number(residualMass);

  if (isNaN(lbm) || isNaN(tbw) || isNaN(rm)) return 0;

  const muscleMass = lbm - (tbw + rm);
  return muscleMass > 0 ? Number(muscleMass.toFixed(2)) : 0;
};




export {
  calculateBMI,
  calculateTheoreticalWeight,
  calculateEstimatedHeight,
  calculateBMR,
  calculateTEE,
  calculateFatMassWeight,
  calculateLeanWeightType,
  calculateLeanBodyMass,
  calculateTotalBodyWater,
  calculateArmMuscleCircumference,
  calculateArmMuscleArea,
  calculateCorrectedArmMuscleArea,
  calculateArmArea,
  calculateArmFatArea,
  calculateEstimatedCurrentWeight,
  calculateWHR,
  calculateResidualMass,
  calculateWeightExcess,
  calculateWeightDeficit,
  calculateIdealWeight,
  calculateCMB,
  calculateAMB,
  calculateAMBc,
  calculateAge,
  calculateMuscleMassWeight,
  hasRequiredSkinfolds,
};
